import { Mail, MessageSquare, Send, Loader2 } from 'lucide-react';
import { useState } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Encode form data for Netlify
      const encode = (data) => {
        return Object.keys(data)
          .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
          .join("&");
      };

      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "contact",
          ...formData
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const mailtoLink = `mailto:hello@limitly.com?subject=Contact from ${formData.name}&body=${encodeURIComponent(formData.message)}`;

  return (
    <div className="pt-24 pb-20">
      <div className="container mx-auto">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold heading-font text-white mb-6">
            Get in touch
          </h1>
          <p className="text-xl text-gray-400 leading-relaxed">
            Have questions about Limitly? We'd love to hear from you.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold heading-font text-white mb-6">
                  Contact information
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10 backdrop-blur-sm">
                    <div className="w-10 h-10 bg-indigo-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail size={20} className="text-indigo-400" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400 mb-1">Email</div>
                      <a href="mailto:hello@limitly.com" className="text-white hover:text-indigo-400 transition-colors">sales@limitly.org

                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10 backdrop-blur-sm">
                    <div className="w-10 h-10 bg-indigo-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare size={20} className="text-indigo-400" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400 mb-1">Response time</div>
                      <div className="text-white">Within 24 hours</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-white/5 rounded-xl border border-white/10 backdrop-blur-sm">
                <h3 className="text-lg font-semibold text-white mb-3">Join our waitlist</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Want to be the first to know when Limitly launches? Join our waitlist for early access and updates.
                </p>
                <a
                  href="/"
                  className="inline-block px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors">

                  Join waitlist
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white/5 rounded-2xl border border-white/10 p-8 backdrop-blur-sm">
              <h2 className="text-2xl font-bold heading-font text-white mb-6">
                Send us a message
              </h2>

              {submitStatus === 'success' ?
              <div className="py-8 text-center space-y-4">
                  <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Message sent!</h3>
                    <p className="text-gray-400">We'll get back to you within 24 hours.</p>
                  </div>
                  <button
                  onClick={() => setSubmitStatus(null)}
                  className="text-indigo-400 hover:text-indigo-300 transition-colors">

                    Send another message
                  </button>
                </div> :

              <form 
                name="contact" 
                method="POST" 
                data-netlify="true" 
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit} 
                className="space-y-5"
              >
                <input type="hidden" name="form-name" value="contact" />
                
                {/* Honeypot field */}
                <p className="hidden">
                  <label>
                    Don't fill this out if you're human: <input name="bot-field" />
                  </label>
                </p>

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Name *
                    </label>
                    <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all"
                    placeholder="Your name" />

                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all"
                    placeholder="you@company.com" />

                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent transition-all resize-none"
                    placeholder="Tell us how we can help..." />

                  </div>

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-600/20 border border-red-600/50 rounded-lg">
                      <p className="text-red-400 text-sm">
                        Something went wrong. Please try again or contact us at support@limitly.org
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-600/50 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2">

                      {isSubmitting ?
                    <>
                          <Loader2 size={18} className="animate-spin" />
                          Sending...
                        </> :

                    <>
                          <Send size={18} />
                          Send message
                        </>
                    }
                    </button>
                    <a
                    href={mailtoLink}
                    className="px-4 py-3 bg-white/5 hover:bg-white/10 text-white font-medium rounded-lg transition-colors border border-white/10 flex items-center justify-center"
                    title="Open in email client">

                      <Mail size={18} />
                    </a>
                  </div>
                </form>
              }
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default Contact;